import { createUuid } from '@notifi/uuid/browser';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import type { VisitRequestFormValues } from '@/components/organisms/scheduler/VisitRequestForm';
import type { AppointmentWizardSlot } from '@/components/organisms/jobs/AppointmentSlotWizard';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { appointmentEndpoints } from '@/lib/api/endpoints';
import { isValidEmail } from '@/lib/validation';
import type { CreateBookingDto } from '@/types/scheduler';
import { useCreateBooking as useSchedulerCreateBooking } from './useCreateBooking';
import { useReserveSlot } from './useReserveSlot';
import { contactHasPhone } from '@/lib/contacts/phone';

export interface CreateBookingFlowResult {
  bookingId?: string;
  /** SERVICE_CALL job eagerly materialized for this request. */
  jobId?: string;
  /** True when the request was created with a schedule (a slot OR an all-day
   *  binding) — false means it went to the unscheduled queue. */
  scheduled: boolean;
}

type ContactLookupResult = {
  id?: string;
  _id?: string;
  contactId?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  companyName?: string | null;
  primaryPhone?: string | null;
  altPhone?: string | null;
  phoneNumbers?: Array<{ number?: string | null }> | null;
};

type ContactLookupResponse =
  | ContactLookupResult[]
  | {
      data?: ContactLookupResult[];
      contacts?: ContactLookupResult[];
      items?: ContactLookupResult[];
    };

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function contactId(contact: ContactLookupResult | null | undefined): string | undefined {
  const id = contact?.id ?? contact?.contactId ?? contact?._id;
  return id ? String(id) : undefined;
}

function contactName(contact: ContactLookupResult): string {
  const personName = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  return contact.displayName?.trim() || personName || contact.companyName?.trim() || '';
}

/** Whole minutes spanned by a planned time, or undefined if it isn't a usable range. */
function planDurationMinutes(startAt: string, endAt: string): number | undefined {
  const start = Date.parse(startAt);
  const end = Date.parse(endAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
  const minutes = Math.round((end - start) / 60_000);
  return minutes > 0 ? minutes : undefined;
}

async function resolveCustomerId(values: VisitRequestFormValues): Promise<string | undefined> {
  if (values.customer.contactId) return values.customer.contactId;

  const search = values.customer.phone?.trim() || values.customer.name?.trim();
  if (!search || search.length < 2) return undefined;

  try {
    const response = await apiGet<ContactLookupResponse>(operationsClient, '/contacts', {
      params: { search, limit: 5 },
    });
    const contacts = Array.isArray(response)
      ? response
      : (response.data ?? response.contacts ?? response.items ?? []);

    const expectedName = normalizeText(values.customer.name);
    // Same number in any spelling, on any of the contact's numbers. Comparing
    // every digit of the primary alone missed the commonest pair: the phone
    // input's `+15125073477` against a stored `(512) 507-3477`.
    const matchedByPhone = contacts.find((contact) =>
      contactHasPhone(contact, values.customer.phone),
    );
    const matchedByName =
      expectedName.length > 0
        ? contacts.find((contact) => normalizeText(contactName(contact)) === expectedName)
        : undefined;

    // Only an EXACT phone or name match binds the booking to an existing
    // customer. `/contacts?search=` is a fuzzy `contains` query, so a lone
    // result is not evidence of identity — it used to be accepted as one, and a
    // single incidental hit ("555" inside an unrelated number) silently bound
    // the visit to the wrong customer, whose name then owned the job. No match
    // means no customerId: the backend creates the contact the caller described.
    return contactId(matchedByPhone ?? matchedByName);
  } catch {
    return undefined;
  }
}

export function useCreateBookingFlow(options?: { jobId?: string; jobPermissions?: boolean }) {
  const createBooking = useSchedulerCreateBooking({
    jobPermissions: Boolean(options?.jobId) || options?.jobPermissions,
  });
  const reserveSlotMutation = useReserveSlot();
  const pendingRequest = useRef<{ fingerprint: string; key: string } | null>(null);

  const submit = useCallback(
    async (
      values: VisitRequestFormValues,
      existingWork?: { jobId: string; idempotencyKey?: string },
      extra: Pick<CreateBookingDto, 'partsOverrideReason'> = {},
    ): Promise<CreateBookingFlowResult> => {
      existingWork = existingWork ?? (options?.jobId ? { jobId: options.jobId } : undefined);
      const fingerprint = JSON.stringify({ values, existingWork, extra });
      if (pendingRequest.current?.fingerprint !== fingerprint) {
        pendingRequest.current = { fingerprint, key: createUuid() };
      }
      const idempotencyKey = existingWork?.idempotencyKey ?? pendingRequest.current.key;
      const customerId = await resolveCustomerId(values);
      const jobContext = Boolean(existingWork?.jobId || options?.jobPermissions);
      const dto: CreateBookingDto = {
        ...(jobContext
          ? {
              customerNotes: values.details.customerNotes.trim() || undefined,
              confirmationMethod: values.details.confirmationMethod,
              confirmationStatus: values.details.confirmationStatus,
            }
          : {}),
        customerId,
        jobId: existingWork?.jobId,
        ...(existingWork && values.durationMinutes
          ? { durationMinutes: values.durationMinutes }
          : {}),
        customerName: values.customer.name,
        phone: values.customer.phone,
        // The form has always collected an email; it used to stop here. Sent so
        // an auto-created contact keeps it, and so a matched contact that has
        // none can be completed with it. The backend validates the format, and
        // a locked-in customer's email is echoed from their contact rather than
        // typed here — so skip anything malformed instead of 400-ing a booking
        // over an existing record the dispatcher cannot even edit.
        email: isValidEmail(values.customer.email) ? values.customer.email.trim() : undefined,
        address: values.addressLine || values.address.street || values.address.zip,
        zipCode: values.address.zip,
        street: values.address.street.trim() || undefined,
        street2: values.address.street2.trim() || undefined,
        city: values.address.city.trim() || undefined,
        state: values.address.state.trim() || undefined,
        country: values.address.country.trim() || undefined,
        // Exact saved-address identity when the user picked one of the
        // customer's configured addresses — carried onto the appointment and
        // the provisioned SERVICE_CALL job so equipment/location features
        // don't have to fuzzy-match the street text back to an address row.
        serviceAddressId: values.serviceAddressId,
        serviceType: values.serviceType,
        urgency: values.urgency,
        constraints: values.constraints,
        customerPreferredTechnicianId: values.customerPreferredTechnicianId,
        customerRequiredTechnicianId: values.customerRequiredTechnicianId,
        jobTypeId: values.jobTypeId,
        // NOT-613 — cadence for a CHOOSE_ON_CREATE recurring job type so the
        // booking instantiates the series instead of dropping to one-off.
        recurrence: values.recurrence,
        issueSummary: values.details.issueSummary.trim() || undefined,
        notes: values.details.notes.trim() || undefined,
        sourceCallControlId: values.sourceCallControlId,
        idempotencyKey,
        promise: values.manualSlot ? values.manualArrivalPromise : undefined,
        ...extra,
      };
      if (values.manualBooking) {
        Object.assign(dto, values.manualBooking);
      } else if (values.windowHold) {
        if (!values.windowHold.accepted || Date.parse(values.windowHold.expiresAt) <= Date.now())
          throw new Error('The selected window needs a current hold and customer acceptance.');
        dto.promise = {
          kind: 'WINDOW',
          startAt: values.windowHold.startAt,
          endAt: values.windowHold.endAt,
        };
        dto.holdId = values.windowHold.holdId;
        dto.skipAutoAssign = values.assignWindowLater || undefined;
        dto.durationMinutes = values.windowHold.durationMinutes;
        const selectedLead = values.crew?.find((member) => member.role === 'LEAD')?.technicianId;
        const selectedHelpers =
          values.crew
            ?.filter((member) => member.role === 'HELPER')
            .map((member) => member.technicianId)
            .sort() ?? [];
        if (
          (values.windowHold.technicianId ?? undefined) !== selectedLead ||
          JSON.stringify(values.windowHold.helperIds ?? []) !== JSON.stringify(selectedHelpers)
        ) {
          throw new Error('Search arrival windows again after changing the crew.');
        }
        if (values.windowHold.technicianId) {
          dto.technicianId = values.windowHold.technicianId;
          dto.helperIds = values.windowHold.helperIds;
        }
      } else if (values.granularity === 'ALL_DAY' || values.granularity === 'DAY_FLEXIBLE') {
        // Scheduling v2 — day-bound request: bind to the chosen day candidate.
        // DAY_FLEXIBLE keeps clock time pending; ALL_DAY derives shift times.
        // Requests→multi-day (consecutiveDays) intentionally unsupported here — a series requires a job.
        if (!values.selectedDay) {
          throw new Error('Select an available day first.');
        }
        dto.granularity = values.granularity;
        dto.scheduledDate = values.selectedDay.date;
        dto.technicianId = values.selectedDay.technicianId;
        if (values.crew?.length) {
          const lead = values.crew.find((member) => member.role === 'LEAD');
          if (!lead || lead.technicianId !== values.selectedDay.technicianId) {
            throw new Error('Search again after changing the crew lead.');
          }
          dto.helperIds = values.crew
            .filter((member) => member.role === 'HELPER')
            .map((member) => member.technicianId);
          if (values.granularity === 'ALL_DAY') dto.crewId = values.savedCrewId;
        }

        if (values.granularity === 'DAY_FLEXIBLE' && values.durationMinutes) {
          dto.durationMinutes = values.durationMinutes;
        }
      } else if (values.unassignedPlan) {
        // Scheduling v2 — manual "Pick a time" with no technician: a planned
        // time, explicitly unassigned. No slot was reserved, so no slot id.
        const { startAt, endAt } = values.unassignedPlan;
        dto.plan = { startAt, endAt };
        dto.technicianId = null;
        // The plan alone does not set the visit's length: the backend computes
        // `durationMinutes` before it resolves the plan, so leaving it off
        // persisted the job/default duration while the plan end said otherwise —
        // the board, capacity checks, ETA propagation and later auto-assignment
        // all read the stored duration, so they over- or under-booked the visit.
        // Derived from the span rather than echoed from `values.durationMinutes`
        // so the number can never contradict the time the dispatcher saw (the
        // form leaves the duration null on the engine default while still
        // planning a concrete hour).
        dto.durationMinutes =
          planDurationMinutes(startAt, endAt) ?? values.durationMinutes ?? undefined;
      } else if (values.selectedSlot?.slotId) {
        if (jobContext) {
          const lead = values.crew?.find((member) => member.role === 'LEAD');
          if (values.crew?.length && lead?.technicianId !== values.selectedSlot.technicianId)
            throw new Error('Search again after changing the crew lead.');
          dto.plan = {
            startAt: values.selectedSlot.startAt,
            endAt: values.selectedSlot.endAt,
            ...(values.crew?.length
              ? {
                  technicianIds: [
                    lead!.technicianId,
                    ...values.crew
                      .filter((member) => member.role === 'HELPER')
                      .map((member) => member.technicianId),
                  ],
                }
              : {}),
          };
          dto.technicianId = values.selectedSlot.technicianId;
        } else dto.selectedSlotId = values.selectedSlot.slotId;
      }
      // The user acknowledged the preflight's conflicts (or confirmed the
      // "Schedule anyway?" dialog) — tell the engine to skip its 409.
      dto.overrideConflicts = values.overrideConflicts || undefined;

      const created = await createBooking.mutateAsync(dto);
      if (pendingRequest.current?.key === idempotencyKey) pendingRequest.current = null;
      const bookingId =
        (created as { bookingId?: string; id?: string }).bookingId ??
        (created as { bookingId?: string; id?: string }).id;
      const jobId = (created as { jobId?: string }).jobId;

      if (bookingId && !jobContext) {
        try {
          await Promise.all([
            apiPatch(operationsClient, appointmentEndpoints.confirmation(bookingId), {
              method: values.details.confirmationMethod,
              status: values.details.confirmationStatus,
            }),
            apiPatch(operationsClient, appointmentEndpoints.customerNotes(bookingId), {
              customerNotes: values.details.customerNotes.trim() || null,
            }),
          ]);
        } catch {
          toast.error('Service call created, but some visit details could not be saved.');
        }
      }

      return {
        bookingId,
        jobId,
        scheduled:
          Boolean(values.manualBooking) ||
          Boolean(values.windowHold) ||
          Boolean(values.selectedSlot) ||
          Boolean(values.unassignedPlan) ||
          values.granularity === 'ALL_DAY' ||
          values.granularity === 'DAY_FLEXIBLE',
      };
    },
    [createBooking, options?.jobId, options?.jobPermissions],
  );

  const reserveSlot = useCallback(
    async (slot: AppointmentWizardSlot, values: VisitRequestFormValues) => {
      const reserved = await reserveSlotMutation.mutateAsync({
        slotId: slot.slotId,
        technicianId: slot.technicianId,
        serviceTypeId: values.serviceTypeId,
        startAt: slot.startAt,
        endAt: slot.endAt,
        score: slot.score,
      });
      // The persisted slot id may differ from the ephemeral availability slotId
      // (the engine reuses an existing slot for the technician+time); return it so
      // the subsequent createBooking references the real slot.
      return reserved.id;
    },
    [reserveSlotMutation],
  );

  return {
    submit,
    reserveSlot,
    isPending: createBooking.isPending || reserveSlotMutation.isPending,
  };
}
